import React from 'react';
import classnames from 'classnames';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { ListItem as MuiListItem, ListSubheader } from '@mui/material';

import { useGroupLabelStyles } from './styles';

interface SidebarGroupHeaderProps {
  /** What the reader sees, e.g. "Resiliency Services". */
  readonly label: string;
  /** The `nav.group` key this header owns, used for the toggle and the qa hook. */
  readonly group: string;
  readonly isCollapsed: boolean;
  readonly onToggle: (group: string) => void;
}

/**
 * A nav group's header: its name, and a chevron that makes the group an
 * accordion.
 *
 * Shared rather than written per persona. The policyholder sidebar had its own
 * pair of hardcoded `ListSubheader`s at a different size and weight, and with no
 * chevron they could not be collapsed at all -- so two sidebars in the same
 * product disagreed about what a nav group looks like and what it does.
 */
export function SidebarGroupHeader({
  label,
  group,
  isCollapsed,
  onToggle,
}: Readonly<SidebarGroupHeaderProps>) {
  const classes = useGroupLabelStyles();

  return (
    <MuiListItem
      button
      onClick={() => onToggle(group)}
      classes={{ root: classes.header }}
      data-qa={`SIDEBAR_GROUP_TOGGLE:${group}`}
    >
      <ListSubheader
        component="span"
        disableSticky
        classes={{ root: classes.label }}
      >
        {label}
      </ListSubheader>
      <ExpandMoreIcon
        fontSize="small"
        className={classnames(classes.chevron, {
          [classes.collapsed]: isCollapsed,
        })}
      />
    </MuiListItem>
  );
}
